import React, { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import {
  getRewardConfig,
  upsertRewardCell,
  getAdminPayouts,
  updatePayoutStatus,
  type RewardCell,
  type AffiliateType,
  type RewardKind,
  type Recurrence,
  type AdminPayout,
} from "../../services/affiliate";

const TYPES: { key: AffiliateType; label: string }[] = [
  { key: "club", label: "Club" },
  { key: "coach", label: "Antrenor" },
  { key: "parent", label: "Părinte" },
  { key: "athlete", label: "Sportiv" },
];
const PLANS = ["start", "campion", "elite"];

type Draft = {
  reward_kind: RewardKind;
  amountEuros: string;
  grant_plan_code: string;
  grant_months: string;
  lives: string;
  recurrence: Recurrence;
  is_active: boolean;
};

const EMPTY: Draft = {
  reward_kind: "money",
  amountEuros: "",
  grant_plan_code: "campion",
  grant_months: "1",
  lives: "",
  recurrence: "one_time",
  is_active: true,
};

function cellToDraft(c?: RewardCell): Draft {
  if (!c) return { ...EMPTY };
  return {
    reward_kind: c.reward_kind,
    amountEuros: c.amount_cents != null ? (c.amount_cents / 100).toString() : "",
    grant_plan_code: c.grant_plan_code || "campion",
    grant_months: c.grant_months != null ? String(c.grant_months) : "1",
    lives: c.lives != null ? String(c.lives) : "",
    recurrence: c.recurrence,
    is_active: c.is_active,
  };
}

export default function AffiliateRewards() {
  const { activeRole } = useAuth();
  const isPlatformAdmin = activeRole === "platform_admin";

  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savedKey, setSavedKey] = useState<string | null>(null);
  const [payouts, setPayouts] = useState<AdminPayout[]>([]);

  const load = async () => {
    setLoading(true);
    try {
      const config = await getRewardConfig();
      const byKey: Record<string, RewardCell> = {};
      config.forEach((c) => (byKey[`${c.affiliate_type}:${c.plan_code}`] = c));
      const next: Record<string, Draft> = {};
      TYPES.forEach((t) => PLANS.forEach((p) => (next[`${t.key}:${p}`] = cellToDraft(byKey[`${t.key}:${p}`]))));
      setDrafts(next);
      setPayouts(await getAdminPayouts());
    } catch (e: any) {
      setError(e?.response?.data?.error || "Nu am putut încărca configurarea.");
    } finally {
      setLoading(false);
    }
  };

  const actPayout = async (id: string, status: "approved" | "paid" | "rejected") => {
    try {
      await updatePayoutStatus(id, status);
      setPayouts(await getAdminPayouts());
    } catch (e: any) {
      setError(e?.response?.data?.error || "Acțiunea a eșuat.");
    }
  };

  useEffect(() => {
    if (isPlatformAdmin) load();
    else setLoading(false);
  }, [isPlatformAdmin]);

  const set = (key: string, patch: Partial<Draft>) => setDrafts((d) => ({ ...d, [key]: { ...d[key], ...patch } }));

  const save = async (type: AffiliateType, plan: string) => {
    const key = `${type}:${plan}`;
    const d = drafts[key];
    setSavingKey(key);
    setError(null);
    setSavedKey(null);
    try {
      await upsertRewardCell({
        affiliate_type: type,
        plan_code: plan,
        reward_kind: d.reward_kind,
        amount_cents: d.reward_kind === "money" ? Math.round((parseFloat(d.amountEuros) || 0) * 100) : undefined,
        grant_plan_code: d.reward_kind === "plan_grant" ? d.grant_plan_code : undefined,
        grant_months: d.reward_kind === "plan_grant" ? parseInt(d.grant_months, 10) || 0 : undefined,
        lives: d.reward_kind === "lives" ? parseInt(d.lives, 10) || 0 : undefined,
        recurrence: d.recurrence,
        is_active: d.is_active,
      });
      setSavedKey(key);
      setTimeout(() => setSavedKey((k) => (k === key ? null : k)), 1500);
    } catch (e: any) {
      setError(e?.response?.data?.error || "Salvarea a eșuat.");
    } finally {
      setSavingKey(null);
    }
  };

  if (!isPlatformAdmin) {
    return (
      <div className="p-6">
        <div className="p-4 text-sm border rounded-xl bg-amber-50 border-amber-200 text-amber-800">
          Doar administratorii platformei au acces la această pagină.
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">
      <AdminPageHeader
        eyebrow="Afiliere"
        title="Recompense afiliați"
        subtitle="Setează ce câștigă fiecare tip de afiliat pentru fiecare plan plătit convertit. Banii (club/antrenor) pot fi recurenți; perks-urile de obicei o dată."
      />

      {error && <div className="mb-4 p-3 text-sm rounded-xl bg-rose-50 border border-rose-200 text-rose-700">{error}</div>}

      {loading ? (
        <div className="py-10 text-center text-slate-400">Se încarcă…</div>
      ) : (
        <div className="space-y-8">
          {TYPES.map((t) => (
            <div key={t.key}>
              <h2 className="mb-2 font-bold text-slate-800">{t.label}</h2>
              <div className="space-y-2">
                {PLANS.map((plan) => {
                  const key = `${t.key}:${plan}`;
                  const d = drafts[key];
                  if (!d) return null;
                  return (
                    <div key={key} className="flex flex-wrap items-end gap-3 p-3 bg-white border rounded-xl border-slate-200">
                      <div className="w-20">
                        <div className="text-[11px] text-slate-400">Plan</div>
                        <div className="font-semibold capitalize text-slate-700">{plan}</div>
                      </div>
                      <label className="text-sm">
                        <span className="text-[11px] text-slate-500 block">Recompensă</span>
                        <select
                          value={d.reward_kind}
                          onChange={(e) => set(key, { reward_kind: e.target.value as RewardKind })}
                          className="px-2 py-1.5 border rounded-lg border-slate-200"
                        >
                          <option value="money">Bani (€)</option>
                          <option value="plan_grant">Luni plan gratis</option>
                          <option value="lives">Vieți</option>
                        </select>
                      </label>

                      {d.reward_kind === "money" && (
                        <label className="text-sm">
                          <span className="text-[11px] text-slate-500 block">Sumă (€)</span>
                          <input type="number" min={0} step="0.01" value={d.amountEuros}
                            onChange={(e) => set(key, { amountEuros: e.target.value })}
                            className="w-24 px-2 py-1.5 border rounded-lg border-slate-200" placeholder="1.20" />
                        </label>
                      )}
                      {d.reward_kind === "plan_grant" && (
                        <>
                          <label className="text-sm">
                            <span className="text-[11px] text-slate-500 block">Plan</span>
                            <select value={d.grant_plan_code} onChange={(e) => set(key, { grant_plan_code: e.target.value })}
                              className="px-2 py-1.5 border rounded-lg border-slate-200">
                              {PLANS.map((p) => <option key={p} value={p} className="capitalize">{p}</option>)}
                            </select>
                          </label>
                          <label className="text-sm">
                            <span className="text-[11px] text-slate-500 block">Luni</span>
                            <input type="number" min={0} value={d.grant_months}
                              onChange={(e) => set(key, { grant_months: e.target.value })}
                              className="w-16 px-2 py-1.5 border rounded-lg border-slate-200" />
                          </label>
                        </>
                      )}
                      {d.reward_kind === "lives" && (
                        <label className="text-sm">
                          <span className="text-[11px] text-slate-500 block">Vieți</span>
                          <input type="number" min={0} value={d.lives}
                            onChange={(e) => set(key, { lives: e.target.value })}
                            className="w-16 px-2 py-1.5 border rounded-lg border-slate-200" />
                        </label>
                      )}

                      <label className="text-sm">
                        <span className="text-[11px] text-slate-500 block">Recurență</span>
                        <select value={d.recurrence} onChange={(e) => set(key, { recurrence: e.target.value as Recurrence })}
                          className="px-2 py-1.5 border rounded-lg border-slate-200">
                          <option value="one_time">O dată</option>
                          <option value="monthly">Lunar</option>
                        </select>
                      </label>
                      <label className="flex items-center gap-1.5 text-sm">
                        <input type="checkbox" checked={d.is_active} onChange={(e) => set(key, { is_active: e.target.checked })}
                          className="w-4 h-4 accent-indigo-600" />
                        <span className="text-slate-600">Activ</span>
                      </label>

                      <button onClick={() => save(t.key, plan)} disabled={savingKey === key}
                        className="px-3 py-1.5 ml-auto text-sm font-semibold text-white rounded-lg bg-indigo-600 disabled:opacity-60">
                        {savingKey === key ? "Se salvează…" : savedKey === key ? "Salvat ✓" : "Salvează"}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          <div>
            <h2 className="mb-2 font-bold text-slate-800">Cereri de retragere</h2>
            {payouts.length === 0 ? (
              <p className="text-sm text-slate-400">Nicio cerere.</p>
            ) : (
              <div className="space-y-2">
                {payouts.map((p) => (
                  <div key={p.id} className="flex flex-wrap items-center gap-3 p-3 bg-white border rounded-xl border-slate-200">
                    <span className="font-mono text-xs text-slate-400">{p.club_id.slice(0, 8)}…</span>
                    <span className="font-bold text-slate-800">{(p.amount_cents / 100).toFixed(2)} €</span>
                    <span className="px-2 py-0.5 text-xs rounded-full bg-slate-100 text-slate-600">{p.status}</span>
                    {p.status === "requested" && (
                      <div className="flex gap-2 ml-auto">
                        <button onClick={() => actPayout(p.id, "approved")} className="px-2.5 py-1 text-xs font-semibold rounded-lg border border-indigo-200 text-indigo-700 hover:bg-indigo-50">Aprobă</button>
                        <button onClick={() => actPayout(p.id, "paid")} className="px-2.5 py-1 text-xs font-semibold rounded-lg border border-emerald-200 text-emerald-700 hover:bg-emerald-50">Marchează plătit</button>
                        <button onClick={() => actPayout(p.id, "rejected")} className="px-2.5 py-1 text-xs font-semibold rounded-lg border border-rose-200 text-rose-600 hover:bg-rose-50">Respinge</button>
                      </div>
                    )}
                    {p.status === "approved" && (
                      <button onClick={() => actPayout(p.id, "paid")} className="ml-auto px-2.5 py-1 text-xs font-semibold rounded-lg border border-emerald-200 text-emerald-700 hover:bg-emerald-50">Marchează plătit</button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
